import React from 'react';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';
import type { WeekPoint } from './userActivityView';

export const SERIES_COLORS = {
    web: '#1F7FA6',
    ios: '#7A5195',
    guest: '#D98C2B',
    activeUsers: '#2c3e50',
};

const SERIES_LABELS: Record<string, string> = {
    web: 'Webb',
    ios: 'iOS',
    guest: 'Gäster och publik',
    activeUsers: 'Aktiva användare',
};

const weekLabel = (week: string): string => {
    const [, m, d] = week.split('-');
    return `${Number(d)}/${Number(m)}`;
};

const WeekTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
    const point = payload?.[0]?.payload as WeekPoint | undefined;
    if (!active || !point) return null;
    const actions = point.web + point.ios + point.guest;
    return (
        <div className="user-activity-tooltip">
            <div className="user-activity-tooltip-title">Veckan från {point.week}</div>
            <div>{point.activeUsers} aktiva användare</div>
            <div>{actions} åtgärder (webb {point.web}, iOS {point.ios}, gäster {point.guest})</div>
        </div>
    );
};

interface Props {
    data: WeekPoint[];
    /** Hide the active-users line (one user's own chart). */
    showActiveUsers?: boolean;
    height?: number;
}

const UserActivityChart: React.FC<Props> = ({ data, showActiveUsers = true, height = 300 }) => (
    <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: showActiveUsers ? 0 : 8, bottom: 0, left: -16 }}>
            <CartesianGrid vertical={false} stroke="#e9ecef" />
            <XAxis
                dataKey="week"
                tickFormatter={weekLabel}
                tick={{ fontSize: 12, fill: '#6c757d' }}
                tickLine={false}
                axisLine={{ stroke: '#dee2e6' }}
                minTickGap={16}
            />
            <YAxis yAxisId="actions" allowDecimals={false} tick={{ fontSize: 12, fill: '#6c757d' }} tickLine={false} axisLine={false} />
            {showActiveUsers && (
                <YAxis yAxisId="users" orientation="right" allowDecimals={false} tick={{ fontSize: 12, fill: '#6c757d' }} tickLine={false} axisLine={false} />
            )}
            <Tooltip content={<WeekTooltip />} cursor={{ fill: 'rgba(31, 127, 166, 0.08)' }} />
            <Legend formatter={(value: string) => SERIES_LABELS[value] ?? value} wrapperStyle={{ fontSize: 13 }} />
            <Bar yAxisId="actions" dataKey="web" stackId="actions" fill={SERIES_COLORS.web} maxBarSize={24} />
            <Bar yAxisId="actions" dataKey="ios" stackId="actions" fill={SERIES_COLORS.ios} maxBarSize={24} />
            <Bar yAxisId="actions" dataKey="guest" stackId="actions" fill={SERIES_COLORS.guest} maxBarSize={24} radius={[4, 4, 0, 0]} />
            {showActiveUsers && (
                <Line yAxisId="users" dataKey="activeUsers" stroke={SERIES_COLORS.activeUsers} strokeWidth={2} dot={false} type="monotone" />
            )}
        </ComposedChart>
    </ResponsiveContainer>
);

export default UserActivityChart;
